import { Button } from '@base-ui/react/button';
import { Tooltip } from '@base-ui/react/tooltip';
import {
  createFileRoute,
  getRouteApi,
  useNavigate,
} from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SeriesOrderDialog } from '@/admin/content/SeriesOrderDialog';
import {
  LANGUAGE_LABELS,
  LANGUAGE_OPTIONS,
  type Language,
} from '@/admin/languages';
import buttonStyles from '@/admin/styles/button.module.css';
import { createTablePage } from '@/admin/ui/createTablePage';
import { Select } from '@/admin/ui/Form';
import { LIST_PAGE_SIZE } from '@/admin/ui/Pagination';
import { CreateSeriesButton } from '@/admin/widgets/CreateSeriesButton';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type SeriesSummary = components['schemas']['SeriesSummary'];

// Optional key rather than an undefined value, so `<Link to="/admin/series/">`
// elsewhere need not pass `search`.
type SeriesSearch = { language?: Language };

const routeApi = getRouteApi('/admin/series/');

const columnHelper = createColumnHelper<SeriesSummary>();

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (c) => i18n.t(LANGUAGE_LABELS[c.getValue()]),
  }),
  columnHelper.accessor('order', {
    header: () => i18n.t('admin.common.order'),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
    cell: (c) => (
      <StatusBadge $published={c.getValue() === 'published'}>
        {c.getValue()}
      </StatusBadge>
    ),
  }),
];

// Language filter and Reorder live in the page header. Order is per language, so
// Reorder needs one chosen.
function SeriesActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = routeApi.useSearch() as SeriesSearch;
  const [ordering, setOrdering] = useState(false);

  return (
    <Actions>
      <Select
        aria-label={t('admin.series.languageFilter')}
        value={language ?? ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const next = e.target.value as Language | '';
          void navigate({
            to: '/admin/series/',
            search: next ? { language: next } : {},
            replace: true,
          });
        }}
      >
        <option value="">{t('admin.series.allLanguages')}</option>
        {LANGUAGE_OPTIONS.map(([code, label]) => (
          <option key={code} value={code}>
            {t(label)}
          </option>
        ))}
      </Select>
      <ReorderButton disabled={!language} onClick={() => setOrdering(true)} />
      {language && (
        <SeriesOrderDialog
          open={ordering}
          language={language}
          onClose={() => setOrdering(false)}
        />
      )}
      <CreateSeriesButton />
    </Actions>
  );
}

// Disabled on "All languages", with the reason on hover and keyboard focus. It
// stays focusable (aria-disabled) so the tooltip can open at all.
function ReorderButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const button = (
    <Button
      className={buttonStyles.Button}
      disabled={disabled}
      focusableWhenDisabled
      onClick={onClick}
    >
      {t('admin.series.reorder')}
    </Button>
  );
  if (!disabled) return button;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger render={button} />
      <Tooltip.Portal>
        <Tooltip.Positioner sideOffset={6}>
          <TipPopup>{t('admin.series.reorderNeedsLanguage')}</TipPopup>
        </Tooltip.Positioner>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

// Rows open the series detail page, where editing and the certificate live —
// matching courses, lessons and topics. A certificate is not a list-level
// concern: most series have one, so a column of it carries almost no signal.
const SeriesTable = createTablePage<SeriesSummary, '/admin/series/$seriesId'>({
  name: 'admin.nav.series',
  useData: (page: number) => {
    const { language } = routeApi.useSearch() as SeriesSearch;
    return api.adminListSeries.useQuery({
      query: {
        limit: LIST_PAGE_SIZE,
        offset: LIST_PAGE_SIZE * page,
        ...(language ? { language } : {}),
      },
    });
  },
  columns,
  itemBaseUrl: '/admin/series/$seriesId',
  getParams: (row) => ({ seriesId: String(row.id) }),
  actions: <SeriesActions />,
});

// Keyed on the filter so the table's own page index starts over with it.
function SeriesList() {
  const { language } = routeApi.useSearch() as SeriesSearch;
  return <SeriesTable key={language ?? 'all'} />;
}

export const Route = createFileRoute('/admin/series/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (s: Record<string, unknown>): SeriesSearch => {
    const language = LANGUAGE_OPTIONS.find(([code]) => code === s.language);
    return language ? { language: language[0] } : {};
  },
  component: SeriesList,
});

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;

  select {
    width: auto;
  }
`;

const TipPopup = styled(Tooltip.Popup)`
  background: var(--grey-1000);
  border-radius: 0.375rem;
  box-shadow: var(--shadow-popover);
  color: var(--component-color);
  font-size: 0.8125rem;
  padding: 0.375rem 0.625rem;
  z-index: 50;
`;

const StatusBadge = styled.span<{ $published?: boolean }>`
  background: ${(p) => (p.$published ? 'var(--success)' : 'var(--grey-200)')};
  border-radius: 999px;
  color: ${(p) => (p.$published ? 'var(--grey-50)' : 'var(--grey-700)')};
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  text-transform: capitalize;
`;
